import { getVisitorId, resolveSession, markBooked as markSessionBooked, type KeyValueStore } from './session';
import type { AnalyticsContext } from './context';

export type AnalyticsEventName =
  | 'wizard_start' | 'step_view' | 'date_selected'
  | 'payment_started' | 'booking_confirmed' | 'date_lost';

export interface AnalyticsEventPayload {
  visitorId: string;
  sessionId: string;
  event: AnalyticsEventName;
  visitorSessionIndex: number;
  clientTs: string;
  step?: string | null;
  mode?: string | null;
  venue?: string | null;
  device?: string | null;
  source?: string | null;
  timeToBookSeconds?: number | null;
  bookingImportedId?: string | null;
  meta?: Record<string, unknown>;
}

export interface AnalyticsClientOptions {
  store: KeyValueStore;
  context: AnalyticsContext;
  now: () => number;
  send: (events: AnalyticsEventPayload[]) => void;
  isOptedOut: () => boolean;
  flushThreshold?: number;
}

export type TrackFields = Partial<Pick<AnalyticsEventPayload,
  'step' | 'mode' | 'venue' | 'bookingImportedId' | 'meta'>>;

export function createAnalyticsClient(opts: AnalyticsClientOptions) {
  const threshold = opts.flushThreshold ?? 10;
  let buffer: AnalyticsEventPayload[] = [];

  function track(event: AnalyticsEventName, fields: TrackFields = {}): void {
    try {
      if (opts.isOptedOut()) return;
      const now = opts.now();
      const visitorId = getVisitorId(opts.store);
      const session = resolveSession(opts.store, now);
      const payload: AnalyticsEventPayload = {
        visitorId,
        sessionId: session.sessionId,
        event,
        visitorSessionIndex: session.visitorSessionIndex,
        clientTs: new Date(now).toISOString(),
        device: opts.context.device,
        source: opts.context.source,
        step: fields.step ?? null,
        mode: fields.mode ?? null,
        venue: fields.venue ?? null,
        bookingImportedId: fields.bookingImportedId ?? null,
        meta: fields.meta,
        timeToBookSeconds: event === 'booking_confirmed'
          ? Math.max(0, Math.round((now - session.startedAt) / 1000))
          : null,
      };
      buffer.push(payload);
      if (buffer.length >= threshold) flush();
    } catch {
      // Analytics must never break the app.
    }
  }

  function flush(): void {
    try {
      if (opts.isOptedOut() || buffer.length === 0) { buffer = []; return; }
      const batch = buffer;
      buffer = [];
      opts.send(batch);
    } catch {
      // swallow — dropping analytics is acceptable, breaking the wizard is not
    }
  }

  function markBooked(): void {
    try { markSessionBooked(opts.store); } catch { /* ignore */ }
  }

  return { track, flush, markBooked };
}
